"use client";

import { InputOTP } from "@repo/ui/components/ui/input-otp";
import { useTranslations } from "next-intl";
import { useState } from "react";
import ClassicLoader from "@/components/common/classic-loader";
import { Button } from "@/components/ui/button";
import { AuthInfoBox } from "@/features/auth/components/elements/auth-info-box";
import { AUTH_PRIMARY_BUTTON } from "@/features/auth/lib/auth-button-styles";

const CODE_LENGTH = 6;

type VerifyCodeStepProps = {
  email: string;
  /** Registration and password reset submit through their own form ids. */
  formId: string;
  isSubmitting: boolean;
  /** Translation key of a code error returned by Clerk. */
  serverError: string | null;
  /** Seconds until the code can be sent again; 0 enables the action. */
  resendSecondsLeft: number;
  /** Hides the inline submit so the host can render it in a bottom bar. */
  submitInFooter?: boolean;
  onSubmit: (code: string) => void;
  onResend: () => void;
};

export const VerifyCodeStep = ({
  email,
  formId,
  isSubmitting,
  serverError,
  resendSecondsLeft,
  submitInFooter = false,
  onSubmit,
  onResend,
}: VerifyCodeStepProps) => {
  const t = useTranslations("auth");
  const translateKey = useTranslations();
  const [code, setCode] = useState("");

  const isCoolingDown = resendSecondsLeft > 0;
  const isComplete = code.length === CODE_LENGTH;

  return (
    <div className="flex flex-col gap-6">
      <AuthInfoBox
        description={t("verifyCode.description", { email })}
        title={t("verifyCode.title")}
      />

      <form
        className="flex flex-col gap-6"
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          if (isComplete) {
            onSubmit(code);
          }
        }}
      >
        <div className="flex flex-col gap-3">
          <InputOTP
            autoFocus
            disabled={isSubmitting}
            error={serverError ? translateKey(serverError) : undefined}
            label={t("verifyCode.inputLabel")}
            length={CODE_LENGTH}
            onChange={setCode}
            // Six digits in means there is nothing left to wait for.
            onComplete={onSubmit}
            value={code}
          />
          <button
            className="self-end text-sm text-text leading-[22px] underline underline-offset-4 hover:no-underline disabled:no-underline disabled:opacity-50 md:self-start"
            disabled={isCoolingDown}
            onClick={onResend}
            type="button"
          >
            {isCoolingDown
              ? t("verifyCode.resendIn", { seconds: resendSecondsLeft })
              : t("verifyCode.resend")}
          </button>
        </div>

        {!submitInFooter && (
          <Button
            className={AUTH_PRIMARY_BUTTON}
            disabled={isSubmitting || !isComplete}
            type="submit"
          >
            {t("verifyCode.submit")}
            {isSubmitting && <ClassicLoader className="ml-2" />}
          </Button>
        )}
      </form>
    </div>
  );
};
